import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { CartItem, Package } from '@/types'
import { v4 as uuidv4 } from '@/utils/uuid'
import { calculateDeposit, hasPhotoService as checkPhotoService } from '@/constants/services'

const API_BASE_URL = '/api/v1'

// Server response codes shared with the Rails API.
export const PHOTO_ASSIGNMENT_LIMIT_CODE = 'photo_service_item_limit_exceeded'
const CART_UNAVAILABLE_CODE = 'cart_unavailable'

interface CartState {
  id: number | null
  itemCount: number
  totalPrice: number
  totalDeposit: number
  isAnonymous: boolean
  bookingDate?: string
  timeSlot?: string
  currentStep?: number
}

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref<CartItem[]>([])
  const bookingDate = ref<string>('')
  const primaryTimeSlot = ref<string>('')
  const currentStep = ref<number>(1)
  const cartState = ref<CartState>({
    id: null,
    itemCount: 0,
    totalPrice: 0,
    totalDeposit: 0,
    isAnonymous: true
  })
  const isLoading = ref(false)
  const isSyncing = ref(false)
  const showPhotoAssignmentLimitModal = ref(false)
  // Set when a change lands while a sync is already in flight. Dropping those
  // changes silently left the server cart behind local state, which then made the
  // server-side slot validation run against a stale cart.
  let syncQueued = false

  // Getters
  const itemCount = computed(() => items.value.length)

  // Raw deposit calculation (before cap)
  const rawTotalDeposit = computed(() => {
    return items.value.reduce((sum, item) => {
      // Skip items without a package (package_id = 0 is temporary)
      if (item.package_id === 0 || !item.package_id) return sum
      return sum + calculateDeposit(item.selected_services)
    }, 0)
  })

  const totalPrice = computed(() => {
    return items.value.reduce((sum, item) => {
      // Skip items without a package (package_id = 0 is temporary)
      if (item.package_id === 0 || !item.package_id) return sum
      return sum + parseFloat(item.price_at_time)
    }, 0)
  })

  // Total deposit cannot exceed the cart total or 1,000 THB.
  const totalDeposit = computed(() => {
    return Math.min(rawTotalDeposit.value, totalPrice.value, 1000)
  })

  const hasPhotoService = computed(() => {
    return items.value.some(item =>
      checkPhotoService(item.selected_services)
    )
  })

  // Get all unique services from all items (merged services)
  // This is used to determine cross-location vs same-location booking
  const mergedServices = computed(() => {
    const services = new Set<string>()
    items.value.forEach(item => {
      if (item.selected_services && item.selected_services.length > 0) {
        item.selected_services.forEach(service => services.add(service))
      }
    })
    return Array.from(services).sort()
  })

  // Check if current cart requires cross-location booking
  // Cross-location = has both studio services (makeup/dress) AND photo service
  const isCrossLocation = computed(() => {
    const services = mergedServices.value
    const hasStudioService = services.some(s => ['makeup', 'dress'].includes(s))
    const hasPhotoService = services.includes('photo')
    return hasStudioService && hasPhotoService
  })

  const isEmpty = computed(() => items.value.length === 0)

  // API Actions

  // Fetch cart from server
  async function fetchCart(): Promise<void> {
    isLoading.value = true
    try {
      const response = await fetch(`${API_BASE_URL}/cart`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // The server repairs carts that predate the photo assignment limit; tell
        // the customer why an item disappeared.
        if (data.warning_code === PHOTO_ASSIGNMENT_LIMIT_CODE) {
          showPhotoAssignmentLimitModal.value = true
        }
        cartState.value = {
          id: data.cart.id,
          itemCount: data.cart.item_count,
          totalPrice: parseFloat(data.cart.total_price),
          totalDeposit: data.cart.total_deposit,
          isAnonymous: data.cart.is_anonymous,
          bookingDate: data.cart.booking_date,
          timeSlot: data.cart.time_slot,
          currentStep: data.cart.current_step
        }

        // Restore booking details from server
        if (data.cart.booking_date) {
          bookingDate.value = data.cart.booking_date
        }
        if (data.cart.time_slot) {
          primaryTimeSlot.value = data.cart.time_slot
        }
        if (data.cart.current_step) {
          currentStep.value = data.cart.current_step
        }

        // Anonymous token is now stored in HTTP-only cookie by the server
        // No need to handle it in JavaScript

        // Get saved items from server
        const savedItems = data.items.map((item: any) => ({
          id: item.id,
          package_id: item.package_id,
          package: item.package,
          person_name: item.person_name,
          age: item.age,
          selected_services: item.selected_services,
          price_at_time: item.price_at_time
        }))

        // Merge with local temporary items (items with package_id = 0)
        // This preserves unsaved person selections
        const tempItems = items.value.filter(item => !item.package_id || item.package_id === 0)

        // Create a map of saved items by ID for deduplication
        const savedItemIds = new Set(savedItems.map((item: CartItem) => item.id))

        // Add saved items
        items.value = [...savedItems]

        // Add temporary items that aren't duplicates
        tempItems.forEach((tempItem) => {
          if (!savedItemIds.has(tempItem.id)) {
            items.value.push(tempItem)
          }
        })
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error)
    } finally {
      isLoading.value = false
    }
  }

  // Sync cart with server
  async function syncCart(): Promise<void> {
    if (isSyncing.value) {
      syncQueued = true
      return
    }

    isSyncing.value = true
    try {
      const syncItems = items.value.map(item => ({
        id: item.id,
        package_id: item.package_id,
        person_name: item.person_name,
        age: item.age,
        age_group: item.package?.age_group,
        gender: item.package?.gender,
        selected_services: item.selected_services,
        price_at_time: item.price_at_time
      }))

      const response = await fetch(`${API_BASE_URL}/cart/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ items: syncItems })
      })

      if (response.ok) {
        const data = await response.json()
        cartState.value = {
          id: data.cart.id,
          itemCount: data.cart.item_count,
          totalPrice: parseFloat(data.cart.total_price),
          totalDeposit: data.cart.total_deposit,
          isAnonymous: data.cart.is_anonymous,
          bookingDate: data.cart.booking_date,
          timeSlot: data.cart.time_slot,
          currentStep: data.cart.current_step
        }

        // Update items from server response (this includes newly saved items with proper IDs)
        if (data.items) {
          const serverItems = data.items.map((item: any) => ({
            id: item.id,
            package_id: item.package_id,
            package: item.package,
            person_name: item.person_name,
            age: item.age,
            selected_services: item.selected_services,
            price_at_time: item.price_at_time
          }))

          // Keep temporary items that weren't saved
          const tempItems = items.value.filter(item => !item.package_id || item.package_id === 0)
          const serverItemIds = new Set(serverItems.map((item: CartItem) => item.id))

          items.value = [...serverItems]

          // Add back temporary items that aren't in server response
          tempItems.forEach((tempItem) => {
            if (!serverItemIds.has(tempItem.id)) {
              items.value.push(tempItem)
            }
          })
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        if (errorData.code === PHOTO_ASSIGNMENT_LIMIT_CODE) {
          showPhotoAssignmentLimitModal.value = true
          await fetchCart()
        } else if (errorData.code === CART_UNAVAILABLE_CODE) {
          // The cart was merged away (usually a login in another tab). Refetching
          // resolves this client onto the cart that replaced it.
          await fetchCart()
        }
      }
    } catch (error) {
      console.error('Failed to sync cart:', error)
    } finally {
      isSyncing.value = false
    }

    // Pick up anything that changed while the request was in flight.
    if (syncQueued) {
      syncQueued = false
      await syncCart()
    }
  }

  // Merge anonymous cart with user cart after login
  // The anonymous token is automatically sent via cookie
  async function mergeAnonymousCart(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/cart/merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      })

      if (response.ok) {
        const data = await response.json().catch(() => ({}))
        if (data.warning_code === PHOTO_ASSIGNMENT_LIMIT_CODE) {
          showPhotoAssignmentLimitModal.value = true
        }
        await fetchCart()
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to merge carts:', error)
      return false
    }
  }

  // Sync booking details (date, time slot, and step) to server
  // Backend will validate slot availability when going to step 3
  async function syncBookingDetails(): Promise<void> {
    const payload = {
      cart: {
        booking_date: bookingDate.value,
        time_slot: primaryTimeSlot.value,
        current_step: currentStep.value
      }
    }

    const response = await fetch(`${API_BASE_URL}/cart/booking`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    if (response.ok) {
      const data = await response.json()
      cartState.value = {
        ...cartState.value,
        bookingDate: data.cart.booking_date,
        timeSlot: data.cart.time_slot,
        currentStep: data.cart.current_step
      }
    } else if (response.status === 409) {
      // Conflict - slot no longer available
      const errorData = await response.json()
      console.error('[Cart] Slot no longer available:', errorData)
      
      // Clear local booking time since it's invalid
      bookingDate.value = ''
      primaryTimeSlot.value = ''
      
      // Throw error to let caller handle
      throw new Error(errorData.error || 'Selected time slot is no longer available')
    } else {
      const errorData = await response.json()
      console.error('[Cart] syncBookingDetails error:', errorData)
      throw new Error(errorData.error || 'Failed to sync booking details')
    }
  }

  // Set current step and sync to server
  async function setCurrentStep(step: number): Promise<void> {
    currentStep.value = step
    await syncBookingDetails()
  }

  // Local Actions
  function addItem(
    pkg: Package,
    personName: string = '',
    age?: number,
    selectedServices?: string[]
  ): boolean {
    const services = selectedServices || pkg.services
    if (wouldExceedPhotoAssignmentLimit(services)) {
      showPhotoAssignmentLimitModal.value = true
      return false
    }

    const item: CartItem = {
      id: uuidv4(),
      package_id: pkg.id,
      package: pkg,
      person_name: personName,
      age,
      selected_services: services,
      price_at_time: pkg.price
    }
    items.value.push(item)

    // Clear booking time when adding new package
    // This ensures user selects fresh availability
    clearBookingTimeThenSync()
    return true
  }

  function removeItem(itemId: string): void {
    const index = items.value.findIndex(item => item.id === itemId)
    if (index > -1) {
      items.value.splice(index, 1)

      // Clear booking time when removing package
      // This ensures user selects fresh availability
      clearBookingTimeThenSync()
    }
  }

  // @returns false only when the photo assignment limit blocked the change.
  function updateItem(itemId: string, updates: Partial<CartItem>): boolean {
    const itemIndex = items.value.findIndex(item => item.id === itemId)
    if (itemIndex === -1) return true

    // Only a change that touches services can claim a second photographer. Editing
    // a name or an age must never be blocked because *another* item books photo —
    // that is what left carts holding two photo items unable to be edited at all.
    if (updates.selected_services !== undefined || updates.package !== undefined) {
      const candidateServices = updates.selected_services
        || updates.package?.services
        || items.value[itemIndex].selected_services
      if (wouldExceedPhotoAssignmentLimit(candidateServices, itemId)) {
        showPhotoAssignmentLimitModal.value = true
        return false
      }
    }

    const oldPackageId = items.value[itemIndex].package_id
    items.value[itemIndex] = { ...items.value[itemIndex], ...updates }

    // Clear booking time if package changed
    // This ensures user selects fresh availability for the new package combination
    if (updates.package_id && updates.package_id !== oldPackageId) {
      clearBookingTimeThenSync()
    } else {
      syncCart()
    }

    return true
  }

  function updatePersonName(itemId: string, name: string): void {
    updateItem(itemId, { person_name: name })
  }

  function updateAge(itemId: string, age: number): void {
    updateItem(itemId, { age })
  }

  function toggleService(itemId: string, serviceCode: string): void {
    const item = items.value.find(item => item.id === itemId)
    if (!item) return

    const index = item.selected_services.indexOf(serviceCode)
    if (index > -1) {
      item.selected_services.splice(index, 1)
    } else {
      if (serviceCode === 'photo' && wouldExceedPhotoAssignmentLimit([...item.selected_services, serviceCode], itemId)) {
        showPhotoAssignmentLimitModal.value = true
        return
      }
      item.selected_services.push(serviceCode)
    }

    syncCart()
  }

  async function setBookingDate(date: string): Promise<void> {
    bookingDate.value = date
    // Sync to server when date changes
    await syncBookingDetails()
  }

  async function setPrimaryTimeSlot(slot: string): Promise<void> {
    primaryTimeSlot.value = slot
    // Sync to server when time slot changes
    await syncBookingDetails()
  }

  // Clear booking date and time slot (when packages change)
  // This forces user to re-select date/time to get fresh availability
  async function clearBookingTime(): Promise<void> {
    bookingDate.value = ''
    primaryTimeSlot.value = ''
    
    // Sync to server to clear booking_date and time_slot
    await syncBookingDetails()
  }

  async function clearCart(): Promise<void> {
    items.value = []
    bookingDate.value = ''
    primaryTimeSlot.value = ''
    currentStep.value = 1
    cartState.value = {
      ...cartState.value,
      currentStep: 1,
      bookingDate: undefined,
      timeSlot: undefined
    }

    try {
      await fetch(`${API_BASE_URL}/cart/clear`, {
        method: 'DELETE',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Failed to clear server cart:', error)
    }
  }

  // Both writes target the same cart row on the server, and the item sync holds a
  // row lock for its whole transaction. Running them in sequence keeps the booking
  // update from queueing behind that lock.
  function clearBookingTimeThenSync(): void {
    clearBookingTime()
      .catch(err => console.error('[Cart] Failed to clear booking time:', err))
      .finally(() => { void syncCart() })
  }

  function wouldExceedPhotoAssignmentLimit(services: string[], excludedItemId?: string): boolean {
    if (!services.includes('photo')) return false

    return items.value.some(item =>
      item.id !== excludedItemId && item.selected_services.includes('photo')
    )
  }

  function openPhotoAssignmentLimitModal(): void {
    showPhotoAssignmentLimitModal.value = true
  }

  function closePhotoAssignmentLimitModal(): void {
    showPhotoAssignmentLimitModal.value = false
  }

  function prepareOrderPayload() {
    return {
      order: {
        booking_date: bookingDate.value,
        studio_slot_time: primaryTimeSlot.value,
        items: items.value
          .filter(item => item.package_id && item.package_id !== 0)
          .map(item => ({
            package_id: item.package_id,
            person_name: item.person_name,
            age: item.age,
            selected_services: item.selected_services
          }))
      }
    }
  }

  return {
    items,
    bookingDate,
    primaryTimeSlot,
    currentStep,
    cartState,
    isLoading,
    isSyncing,
    showPhotoAssignmentLimitModal,
    itemCount,
    rawTotalDeposit,
    totalDeposit,
    totalPrice,
    hasPhotoService,
    mergedServices,
    isCrossLocation,
    isEmpty,
    // API actions
    fetchCart,
    syncCart,
    mergeAnonymousCart,
    syncBookingDetails,
    // Local actions
    addItem,
    removeItem,
    updateItem,
    updatePersonName,
    updateAge,
    toggleService,
    setBookingDate,
    setPrimaryTimeSlot,
    setCurrentStep,
    clearCart,
    clearBookingTime,
    openPhotoAssignmentLimitModal,
    closePhotoAssignmentLimitModal,
    prepareOrderPayload
  }
})
